import { memo, useMemo, useState } from "react";
import { Button, Form, FormField, FormGroup, Icon, Input } from "semantic-ui-react";

// Components
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { Table } from "react-lib/frameworks/Table";
import PaginationShorthand from "react-lib/appcon/common/PaginationShorthand";

// Types
import {
  SetProp,
  CustomerListData,
  PickedState,
  GetCustomerListParams,
  DEFAULT_LIST_LIMIT,
  BTN_ACTS,
} from "./types/MeBillingType";
import { RowInfo } from "react-table-6";

type CardMeBillingCustomerListProps = {
  //
  setProp: SetProp;
  // data
  customerList: CustomerListData | undefined;

  // callbacks
  onSelectCustomer: (customer: RowInfo["original"]) => void;
  onSearch: (filters: GetCustomerListParams) => void;
  onPageChange: (activePage: number) => void;
  onOpenNewCustomer: () => void;

  // Common
  languageUX: "en" | "th";

  // config
  isHideButtonNewCustomer?: boolean;
  isShowFilterMobileNumber?: boolean;
} & Pick<PickedState, "buttonLoadCheck" | "loadingStatus">;

const CardMeBillingCustomerList = (props: CardMeBillingCustomerListProps) => {
  const [filters, setFilters] = useState<GetCustomerListParams>({
    patient_name_search: "",
    identity_document_number: "",
    phone_no: "",
  });

  const handleSearch = () => {
    props.onSearch(filters);
  };

  const handleFilterChange = (key: keyof GetCustomerListParams, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // todo ---- แสดง Icon Mobile เมื่อมีการเชื่อมกับ User แล้ว

  const formattedCustomerList = useMemo(() => {
    return (props.customerList?.items || []).map((item) => ({
      ...item,
      division_hn: (
        <span>
          {item.patient_detail?.division_hn || ""}
          {item.lock_hn ? <Icon name="mobile alternate" color="pink" /> : null}
        </span>
      ),
      full_name: (
        <span>
          {item.pre_name}
          {item.first_name} {item.last_name}
        </span>
      ),
      birthdate: item?.patient_detail?.birthdate || "",
      gender: item?.patient_detail?.gender_label || "",
      // division_hn: item?.patient_detail?.division_hn || "",
      citizen_passport: item?.patient_detail?.citizen_passport || "",
      email: item?.patient_detail?.email || "",
      line_id: item?.patient_detail?.line_id || "",
      present_address_full: item?.patient_detail?.present_address_full || "",
    }));
  }, [props.customerList?.items]);

  return (
    <div style={{ padding: "15px 10px", height: "100%" }}>
      {/* --- Filter --- */}
      <Form>
        <FormGroup inline style={{ margin: 0 }}>
          <FormField width={3}>
            <label>ชื่อ - นามสกุลผู้รับบริการ</label>
          </FormField>
          <FormField width={3}>
            <label>เลขที่เอกสารระบุตัวตน</label>
          </FormField>
          <FormField
            width={3}
            style={{ ...(!props.isShowFilterMobileNumber && { display: "none" }) }}
          >
            <label>หมายเลขโทรศัพท์</label>
          </FormField>
        </FormGroup>
        <FormGroup inline>
          <FormField width={3}>
            <div style={{ width: "100%" }}>
              <Input
                fluid
                icon="search"
                placeholder="ค้นหาชื่อ - นามสกุล"
                value={filters.patient_name_search}
                onChange={(e, { value }) => handleFilterChange("patient_name_search", value)}
              />
            </div>
          </FormField>
          <FormField width={3}>
            <div style={{ width: "100%" }}>
              <Input
                fluid
                icon="search"
                placeholder="ค้นหาเลขที่เอกสารระบุตัวตน"
                value={filters.identity_document_number}
                onChange={(e, { value }) => handleFilterChange("identity_document_number", value)}
              />
            </div>
          </FormField>
          <FormField
            width={3}
            style={{ ...(!props.isShowFilterMobileNumber && { display: "none" }) }}
          >
            <div style={{ width: "100%" }}>
              <Input
                fluid
                icon="search"
                placeholder="หมายเลขโทรศัพท์"
                value={filters.phone_no}
                onChange={(e, { value }) => handleFilterChange("phone_no", value)}
              />
            </div>
          </FormField>
          <FormField width={2}>
            <div style={{ width: "100%" }}>
              <ButtonLoadCheck
                // callback
                setProp={props.setProp}
                onClick={handleSearch}
                // data
                paramKey={BTN_ACTS.GET_CUSTOMER_LIST}
                buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.GET_CUSTOMER_LIST]}
                // config
                fluid
                title={"ค้นหาข้อมูล"}
                style={{ backgroundColor: "var(--primary-theme-color)", color: "white" }}
              />
            </div>
          </FormField>
          <FormField style={{ ...(props.isHideButtonNewCustomer && { display: "none" }) }}>
            <div style={{ width: "100%" }}>
              <Button
                fluid
                style={{
                  backgroundColor: "#87b5ff",
                  color: "white",
                }}
                onClick={props.onOpenNewCustomer}
              >
                <Icon name="plus" />
                เปิดประวัติผู้รับบริการใหม่
              </Button>
            </div>
          </FormField>
        </FormGroup>
      </Form>

      {/* --- Table --- */}
      <Table
        classname="mebilling-list"
        data={formattedCustomerList}
        headers="หมายเลขผู้รับบริการ, ชื่อผู้รับบริการ, เพศ, วันเดือนปีเกิด, เลขที่เอกสารระบุตัวตน, หมายเลขโทรศัพท์, อีเมล, ที่อยู่"
        keys="division_hn, full_name, gender, birthdate, citizen_passport, phone_no, email, present_address_full"
        minRows={DEFAULT_LIST_LIMIT}
        showPagination={false}
        getTrProps={(state: any, row: RowInfo) => ({
          onClick: () => props.onSelectCustomer(row.original),
        })}
        style={{ height: "calc(76vh - 2rem)" }}
        widths="100, ^120, 120, 120, 160, 120, ^120, ^120"
      />

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <PaginationShorthand
          activePage={props?.customerList?.activePage || 1}
          limit={DEFAULT_LIST_LIMIT}
          total={props?.customerList?.total || 0}
          onPageChange={props.onPageChange}
        />
      </div>
    </div>
  );
};

export default memo(CardMeBillingCustomerList);
